// src/business/pages/hr/HREmployees.jsx
// HR › Employees (UI-3E): everyone who works or worked here, with the people waiting to start above the list.
// The list opens each person's record; lifecycle actions live on the record, where their facts are.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, UserPlus } from "lucide-react";
import { businessApi } from "../../../services/api";
import { recruitmentApi } from "../../../services/recruitmentService";
import { useBusinessAuth } from "../../context/BusinessAuthContext";
import DirectHireModal from "./components/DirectHireModal";
import DatePickerInput, { todayLocalISO } from "../../../components/DatePickerInput";
import { isCandidateReadyForActivation } from "./candidateActivationReadiness";
import { stagedPayText } from "./compensationView";
import { formatDay } from "./myHrText";
import {
  Button, ConfirmDialog, EmptyState, ErrorState, Field, Notice, PageHeader, SearchInput, Select, SkeletonRows, Status, plainError,
  Surface, Table, TableContainer, TBody, Td, Th, THead, Tr, cn, focusRing, rowOpens,
} from "../../../components/ui";
import {
  STATUS_FILTERS, departmentOptions, filterMembers, isEmployeeRecord, isFormerMember, isInvitation, roleName, roleOptions, statusWord,
} from "./employeeView";

const candidateName = (candidate) =>
  [candidate.applicant_first_name, candidate.applicant_last_name].filter(Boolean).join(" ") || candidate.applicant_email_normalized;

function StatusCell({ member, leavingOn }) {
  const word = statusWord(member);
  return (
    <div>
      <Status tone={word.tone} icon={word.tone !== "neutral"} className="text-body-sm">{word.label}</Status>
      {leavingOn && !isFormerMember(member) ? (
        <div className="text-meta text-secondary">Leaving {formatDay(leavingOn, { year: true })}</div>
      ) : null}
      {isFormerMember(member) && member.lastWorkingDate ? (
        <div className="text-meta text-tertiary">Last day {formatDay(member.lastWorkingDate, { year: true })}</div>
      ) : null}
    </div>
  );
}

/** Approved candidates waiting for their start: activation and start-date changes (Recruitment's hand-off). */
function AwaitingActivation({ queue, error, canActivate, canChangeStartDate, onActivated, onNotice }) {
  const [editing, setEditing] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [confirm, setConfirm] = useState(null);
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Manila", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

  if (!queue.length && !error) return null;

  const activate = async (candidate) => {
    const response = await recruitmentApi.activateCandidate(candidate.application_id);
    setConfirm(null);
    onNotice(response.result?.accessEmailSent === false
      ? { tone: "warning", text: `${candidateName(candidate)} is now an active employee, but the dashboard access email failed. Resend it from Recruitment.` }
      : { tone: "success", text: `${candidateName(candidate)} is now an active employee. Dashboard access instructions were emailed.` });
    await onActivated();
  };

  const changeDate = async (candidate) => {
    await recruitmentApi.changeCandidateStartDate(candidate.application_id, newDate);
    setConfirm(null);
    setEditing(null);
    onNotice({ tone: "success", text: `${candidateName(candidate)}'s start date is now ${formatDay(newDate, { year: true })}.` });
    await onActivated();
  };

  return (
    <section aria-labelledby="awaiting-title" className="space-y-3">
      <h2 id="awaiting-title" className="text-section-title text-foreground">Waiting to start</h2>
      {error ? <Notice tone="danger" role="alert">{error}</Notice> : null}
      <Surface as="ul" className="divide-y divide-subtle">
        {queue.map((candidate) => {
          const setup = candidate.business_details || {};
          const ready = isCandidateReadyForActivation(candidate, today);
          const name = candidateName(candidate);
          return (
            <li key={candidate.application_id} className="space-y-3 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-panel-title text-foreground">{name}</p>
                  <p className="text-body-sm text-secondary">
                    {[candidate.position_title, candidate.role_name, candidate.department_name].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-meta text-tertiary">
                    {setup.startDate ? `Starts ${formatDay(setup.startDate, { year: true })}` : "No start date yet"}
                    {" · "}
                    {Number(candidate.requirements_required) ? `${candidate.verified_count} of ${candidate.requirement_count} documents verified` : "No documents required"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {canChangeStartDate ? (
                    <Button size="sm" onClick={() => { setEditing(candidate.application_id); setNewDate(setup.startDate || ""); }}>Change start date</Button>
                  ) : null}
                  {canActivate ? (
                    <Button size="sm" variant="primary" disabled={!ready} onClick={() => setConfirm({ kind: "activate", candidate })}>Activate employee</Button>
                  ) : null}
                </div>
              </div>
              {canActivate && !ready ? (
                <p className="text-meta text-tertiary">Activation opens on the start date once the required documents are verified.</p>
              ) : null}
              {editing === candidate.application_id ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <Field label="New start date" className="sm:w-64">
                    <DatePickerInput value={newDate} onValueChange={setNewDate} min={todayLocalISO()} title="Select start date" ariaLabel={`New start date for ${name}`} />
                  </Field>
                  <div className="flex gap-2">
                    <Button size="sm" variant="primary" disabled={!newDate || newDate === setup.startDate || newDate < todayLocalISO()} onClick={() => setConfirm({ kind: "date", candidate })}>
                      Save start date
                    </Button>
                    <Button size="sm" variant="quiet" onClick={() => setEditing(null)}>Cancel</Button>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </Surface>

      {confirm?.kind === "activate" ? (
        <ConfirmDialog
          title={`Activate ${candidateName(confirm.candidate)}?`}
          facts={[
            { label: "Role", value: [confirm.candidate.position_title, confirm.candidate.role_name].filter(Boolean).join(" · ") || "Not set" },
            { label: "Department", value: confirm.candidate.department_name || "Not set" },
            { label: "Starts", value: formatDay(confirm.candidate.business_details?.startDate, { year: true }) },
            { label: "Pay", value: stagedPayText(confirm.candidate.business_details || {}) || "Set by HR" },
          ]}
          consequence="Their employment becomes active and they are sent dashboard access by email. Payroll pays them once their weekly schedule is set in HR › Shifts (if the hiring did not set it)."
          confirmLabel="Activate employee"
          cancelLabel="Not yet"
          onConfirm={() => activate(confirm.candidate)}
          onClose={() => setConfirm(null)}
        />
      ) : null}
      {confirm?.kind === "date" ? (
        <ConfirmDialog
          title={`Change ${candidateName(confirm.candidate)}'s start date?`}
          facts={[
            { label: "Current start", value: formatDay(confirm.candidate.business_details?.startDate, { year: true }) },
            { label: "New start", value: formatDay(newDate, { year: true }) },
          ]}
          consequence="They can be activated on or after the new date."
          confirmLabel="Change start date"
          cancelLabel="Keep date"
          onConfirm={() => changeDate(confirm.candidate)}
          onClose={() => setConfirm(null)}
        />
      ) : null}
    </section>
  );
}

export default function HREmployees() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useBusinessAuth();
  const owner = String(auth?.role || "").toUpperCase() === "OWNER";
  const can = (permission) => owner || Boolean(auth?.hasPermission?.(permission));
  const canActivate = can("hr:onboarding:activate") || can("members:update_role");
  const canChangeStartDate = can("hr:onboarding:approve") || can("members:update_role");
  const canDirectHire = can("members:invite");

  const [members, setMembers] = useState([]);
  const [queue, setQueue] = useState([]);
  const [queueError, setQueueError] = useState("");
  const [leaving, setLeaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [showDirectHire, setShowDirectHire] = useState(false);
  const [cancelInvite, setCancelInvite] = useState(null);
  const [filters, setFilters] = useState({
    status: searchParams.get("status") === "former" ? "former" : "current", role: "", department: "", query: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await businessApi.getMembers();
      setMembers(Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []);
      setError("");
    } catch (err) {
      setError(plainError(err, "Check your connection and try again."));
    } finally {
      setLoading(false);
    }
    recruitmentApi.activationQueue()
      .then((pending) => { setQueue(pending.candidates || []); setQueueError(""); })
      .catch(() => setQueueError("People waiting to start could not be loaded."));
    businessApi.getSeparations({ status: "scheduled" })
      .then((res) => setLeaving(Object.fromEntries((res?.separations || []).map((row) => [row.memberId, row.lastWorkingDate]))))
      .catch(() => setLeaving({}));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Employees are the members the business keeps an employment record for (or is hiring); a member without one, such
  // as an owner who is not on the payroll, has access to the business but is not listed as an employee (APP-HR-109).
  const employees = useMemo(() => members.filter(isEmployeeRecord), [members]);
  const membersOnly = useMemo(() => members.filter((member) => !isEmployeeRecord(member)), [members]);
  const roles = useMemo(() => roleOptions(employees), [employees]);
  const departments = useMemo(() => departmentOptions(employees), [employees]);
  const rows = useMemo(() => filterMembers(employees, filters), [employees, filters]);
  const set = (key) => (event) => setFilters((current) => ({ ...current, [key]: event.target.value }));
  const filtered = filters.role || filters.department || filters.query.trim() || filters.status !== "current";

  const revokeInvite = async () => {
    await businessApi.revokeBusinessInvitation(cancelInvite.invitationId);
    setNotice({ tone: "success", text: `The invitation for ${cancelInvite.email} was cancelled.` });
    setCancelInvite(null);
    await load();
  };

  const toolbar = (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_180px_200px_180px]">
      <SearchInput label="Search employees" placeholder="Search by name, email, code or role" value={filters.query} onChange={set("query")} />
      <Select aria-label="Show" value={filters.status} onChange={set("status")}>
        {STATUS_FILTERS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
      <Select aria-label="Role" value={filters.role} onChange={set("role")}>
        <option value="">All roles</option>
        {roles.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
      <Select aria-label="Department" value={filters.department} onChange={set("department")}>
        <option value="">All departments</option>
        {departments.map((name) => <option key={name} value={name}>{name}</option>)}
      </Select>
    </div>
  );

  const openable = (member) => !isInvitation(member);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Employees"
        description="Everyone who works here, and the records of those who left. Open a person to see or change their employment."
        actions={canDirectHire ? <Button variant="primary" icon={UserPlus} onClick={() => setShowDirectHire(true)}>Add employee</Button> : null}
      />
      {notice ? <Notice tone={notice.tone} role="status">{notice.text}</Notice> : null}

      <AwaitingActivation
        queue={queue}
        error={queueError}
        canActivate={canActivate}
        canChangeStartDate={canChangeStartDate}
        onActivated={load}
        onNotice={setNotice}
      />

      <section aria-labelledby="employees-title" className="space-y-3">
        <h2 id="employees-title" className="sr-only">Employee list</h2>
        <Surface toolbar={toolbar}>
          {loading ? (
            <SkeletonRows rows={5} columns={5} label="Loading employees" />
          ) : error ? (
            <ErrorState title="The employee list did not load" description={error} onRetry={load} />
          ) : !rows.length ? (
            <EmptyState
              title={filtered ? "No one matches these filters" : "No employees yet"}
              description={filtered ? "Clear the search or choose another filter." : "People hired through Recruitment appear here once they start."}
            />
          ) : (
            <>
              <TableContainer className="relative hidden rounded-b-panel md:block">
                <Table caption="Employees">
                  <THead>
                    <Tr>
                      <Th>Employee</Th>
                      <Th>Role</Th>
                      <Th>Department</Th>
                      <Th>Status</Th>
                      <Th>Started</Th>
                      <Th><span className="sr-only">Actions</span></Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {rows.map((member) => (
                      <Tr key={member.id} {...(openable(member) ? rowOpens(() => navigate(`/business/hr/employees/${member.id}`)) : {})}>
                        <Td>
                          {openable(member) ? (
                            <Link to={`/business/hr/employees/${member.id}`} className={cn("rounded-control font-medium text-foreground hover:underline", focusRing)}>{member.name}</Link>
                          ) : <span className="font-medium text-foreground">{member.email}</span>}
                          <div className="text-meta text-tertiary">{[member.employeeCode, member.email].filter(Boolean).join(" · ")}</div>
                        </Td>
                        <Td>{roleName(member) || "—"}</Td>
                        <Td>{member.department || "—"}</Td>
                        <Td><StatusCell member={member} leavingOn={leaving[member.id]} /></Td>
                        <Td className="whitespace-nowrap text-secondary">{member.hireDate ? formatDay(String(member.hireDate).slice(0, 10), { year: true }) : "—"}</Td>
                        <Td actions>
                          {isInvitation(member) && member.invitationId ? (
                            <Button size="sm" variant="quiet" onClick={(event) => { event.stopPropagation(); setCancelInvite(member); }}>Cancel invitation</Button>
                          ) : openable(member) ? <ChevronRight aria-hidden="true" className="ml-auto h-4 w-4 text-tertiary" /> : null}
                        </Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </TableContainer>
              <ul className="divide-y divide-subtle md:hidden" aria-label="Employees">
                {rows.map((member) => (
                  <li key={member.id}>
                    {openable(member) ? (
                      <Link to={`/business/hr/employees/${member.id}`} className={cn("flex min-h-touch items-center gap-3 px-4 py-3 hover:bg-hover", focusRing, "focus-visible:outline-offset-[-2px]")}>
                        <div className="min-w-0 flex-1">
                          <div className="text-body font-medium text-foreground">{member.name}</div>
                          <div className="text-meta text-secondary">{[roleName(member), member.department].filter(Boolean).join(" · ") || "No role yet"}</div>
                          <StatusCell member={member} leavingOn={leaving[member.id]} />
                        </div>
                        <ChevronRight aria-hidden="true" className="h-4 w-4 shrink-0 text-tertiary" />
                      </Link>
                    ) : (
                      <div className="flex items-center justify-between gap-3 px-4 py-3">
                        <div className="min-w-0">
                          <div className="truncate text-body text-foreground">{member.email}</div>
                          <div className="text-meta text-secondary">Invited</div>
                        </div>
                        {member.invitationId ? <Button size="sm" variant="quiet" onClick={() => setCancelInvite(member)}>Cancel invitation</Button> : null}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Surface>
        {!loading && !error && rows.length ? (
          <p className="text-meta text-tertiary">{rows.length === 1 ? "1 person" : `${rows.length} people`}</p>
        ) : null}
        {!loading && !error && membersOnly.length ? (
          <p className="max-w-[75ch] text-meta text-secondary">
            Not listed as {membersOnly.length === 1 ? "an employee" : "employees"}:{" "}
            {membersOnly.map((member, index) => (
              <span key={member.id}>
                {index ? ", " : ""}
                <Link to={`/business/hr/employees/${member.id}`} className={cn("rounded-control font-medium text-accent hover:underline", focusRing)}>{member.name || member.email}</Link>
                {roleName(member) ? ` (${roleName(member)})` : ""}
              </span>
            ))}
            . {membersOnly.length === 1 ? "This person has" : "They have"} access to the business but no employment record.
          </p>
        ) : null}
      </section>

      {cancelInvite ? (
        <ConfirmDialog
          title={`Cancel the invitation for ${cancelInvite.email}?`}
          facts={[{ label: "Invited as", value: roleName(cancelInvite) || "Not set" }]}
          consequence="The invitation link stops working. They can still be hired later through Recruitment."
          confirmLabel="Cancel invitation"
          cancelLabel="Keep invitation"
          tone="danger"
          onConfirm={revokeInvite}
          onClose={() => setCancelInvite(null)}
        />
      ) : null}

      {showDirectHire ? (
        <DirectHireModal
          onClose={() => setShowDirectHire(false)}
          onCreated={(result) => {
            setShowDirectHire(false);
            setNotice(result?.emailSent
              ? { tone: "success", text: "Direct hire created. The onboarding email was sent." }
              : { tone: "warning", text: "Direct hire created, but the email failed. Open the candidate in Recruitment to resend onboarding." });
            load();
            if (result?.applicationId) navigate(`/business/hr/recruitment/applicants/${result.applicationId}`);
          }}
        />
      ) : null}
    </div>
  );
}
